import { HttpErrorResponse } from '@angular/common/http';
import { ITranslationService } from '@kikkz/shared/i18n/util';
import { ILoggingService } from '@kikkz/shared/logging/util';

/** Backend error envelope: `{ errorCode, errorMessage }` (errorMessage is technical English). */
interface BackendErrorBody {
  errorCode?: number;
  errorMessage?: string;
}

/** Translation keys for the friendly, user-facing copy of the backend's known `errorCode`s. */
const ERROR_CODE_KEYS: Record<number, string> = {
  // Prediction no longer editable — the client state is stale (deadline passed server-side).
  50000: 'pg.errors.code.50000',
  50001: 'pg.errors.code.50001',
  50002: 'pg.errors.code.50002',
  // Season prediction deadline passed server-side.
  50003: 'pg.errors.code.50003',
};

/**
 * Resolves a user-facing message from a backend error: a plain-string body (already
 * user-facing, supplied by the backend — returned verbatim, never translated), then the
 * friendly copy of a known `errorCode`, otherwise `fallbackKey`. The raw (technical,
 * English) `errorMessage` is never shown, nor is Angular's generic
 * `HttpErrorResponse.message`.
 *
 * Shared by every prediction-game store (and the tenant stores built on top of them) so
 * the error-code dictionary exists exactly once.
 *
 * This resolves to the final STRING here rather than handing the store a translation key:
 * the store's `error` state feeds both `kikkz-async-content`'s truthiness check and the
 * snackbar message. Keeping it one plain string avoids rippling a second message type
 * through every event payload, every reducer and every consumer of the error state.
 *
 * When the body carries an `errorCode` the dictionary above does not know, this warns
 * once via `logger` before falling back to `fallbackKey` — so a new backend error code
 * shows up in the logs instead of silently rendering as the generic fallback text. That
 * is why `logger` is required: an optional one turns the warning into a promise the
 * caller can quietly break.
 */
export function serverErrorMessage(
  error: unknown,
  fallbackKey: string,
  translation: ITranslationService,
  logger: ILoggingService,
): string {
  const body = (error as HttpErrorResponse)?.error;
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (body && typeof body === 'object') {
    const { errorCode: code } = body as BackendErrorBody;
    if (typeof code === 'number' && ERROR_CODE_KEYS[code]) {
      return translation.translate(ERROR_CODE_KEYS[code]);
    }
    if ('errorCode' in body) {
      const status = (error as HttpErrorResponse)?.status;
      logger.warn('[serverErrorMessage] Unknown backend errorCode', { code, status });
    }
  }
  return translation.translate(fallbackKey);
}

/**
 * Collapse a burst of rapid prediction edits (e.g. dragging a dial, mashing +/-) into a
 * single persisted save once the user pauses. Debounced PER FIXTURE — see
 * `FixturesStore.savePrediction$`.
 */
export const SAVE_DEBOUNCE_MS = 400;
